import { Injectable } from '@angular/core';

import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';

import { ButtonPropsModel, Dialog, DialogModel } from '@syncfusion/ej2-angular-popups';
import { ElementProperties, createElement } from '@syncfusion/ej2-base';

import { PromptAction, PromptButton, PromptOptions, IElementOptions } from './cer-ui-prompt.service';
import { CerLocaleService } from './cer-ui-locale.service';

export function createDialogElement(tagName: string, options?: IElementOptions, parent: HTMLElement = null): HTMLElement {
  const ele = createElement(tagName, options as ElementProperties);
  if (options) {
    const o = options;
    if (o.innerText) {
      ele.innerText = o.innerText;
    }
    if (o.placeholder?.length > 0) {
      (<any>ele).placeholder = o.placeholder;
    }
    if (o.type?.length > 0) {
      (<any>ele).type = o.type;
    }
    if (o.accept?.length > 0) {
      (<any>ele).accept = o.accept;
    }
    if (o.src?.length > 0) {
      (<any>ele).src = o.src;
    }
  }
  if (parent) {
    parent.appendChild(ele);
  }
  return ele;
}

@Injectable({
  providedIn: 'root'
})
export class CerUiPromptProviderService {

  constructor(private matSnackBar: MatSnackBar, private localeService: CerLocaleService) { }


  // Format text as HTML for use in Prompt + Toast
  public textToHtml(text: string | string[], asList: boolean = false): string {
    return asList ? CerLocaleService.textAsHtmlList(text)
      : this.localeService.textToHtml(text);
  }

  // MatSnackbar used as "Toast"
  public toast(content: string, title?: string, duration: number = undefined, actionCallBack: any = null): MatSnackBarRef<TextOnlySnackBar> {
    if (content?.length > 0) {
      const ref = this.matSnackBar.open(content, title, { duration: duration });
      if (actionCallBack != null) {
        ref.onAction().subscribe(action => actionCallBack(action));
      }
      return ref;
    }
    return null;
  }

  public toastHide() {
    this.matSnackBar.dismiss();
  }

  public getDialogContent(): HTMLDivElement {
    return (<any>this.sfDialog).contentEle;
  }

  // Dialog used as "Prompt"
  public prompt(content: string | string[] | HTMLElement, title: string, options: PromptOptions = null): Promise<PromptAction> {
    return new Promise<PromptAction>(resolve => {
      const defaultAction = options?.defaultAction ?? 'ok';
      var buttons = options?.buttons ?? [];
      const btnClose = buttons.find(b => b.action != 'clickOnly');
      if (!btnClose) {
        buttons = [CerUiPromptProviderService.promptBtnClose].concat(buttons);
      }
      this.dlgHideResolver = () => {
        resolve(defaultAction);
      }
      const btns: ButtonPropsModel[] = []
      buttons?.forEach(b => {
        b.click ??= CerUiPromptProviderService.promptBtnNoOpClick;
        const btn: ButtonPropsModel = {
          buttonModel: {
            content: b.text,
            iconCss: b.iconCss
          },
          type: b.submit === true ? 'submit' : 'button',
          click: (b.action == 'clickOnly') ?
            (click) => { this.dlgHideResolver = null; b.click(click); resolve(b.action); } :
            (click) => { this.dlgHideResolver = null; this.promptHide(); b.click(click); resolve(b.action) }
        };
        if (b.action == defaultAction) {
          btn.buttonModel.cssClass = "e-primary";
        }
        btns.push(btn);
      });
      if (btns?.length == 1) {
        btns[0].buttonModel.cssClass = "e-primary";
      }

      this.promptDlg(content, title, defaultAction, options?.dialogArgs, btns);

      if (options?.inputs?.length > 0) {

      }
      /*      if (bodyElement != null) {
        (<any>this.sfDialog).contentEle.appendChild(bodyElement);
      }
    });*/


    });
  }

  public static promptBtnNoOpClick(e: any) {
  }

  public static promptBtnOk: PromptButton = {
    text: 'OK',
    action: 'ok',
    submit: true
  };

  public static promptBtnCancel: PromptButton = {
    text: 'Annuller',
    action: 'cancel'
  };

  public static promptBtnClose: PromptButton = {
    text: 'Luk',
    action: 'close'
  };


  private dlgHideResolver: any;
  private sfDialog: Dialog;
  public promptHide() {
    if (this.dlgHideResolver) {
      this.dlgHideResolver();
      this.dlgHideResolver = null;
    }
    this.sfDialog?.hide();
    this.sfDialog = null;
  }

  public promptClose() {
    this.sfDialog?.close();
  }

  public promptSetPosition() {
    if (this.sfDialog) {
      (<any>this.sfDialog).positionChange();
    }
  }

  public promptAutoOk(): Promise<PromptAction> {
    var autoPromise = new Promise<PromptAction>((resolve, reject) => {
      resolve('ok');
    });
    return autoPromise;
  }

  private promptDlg(content: string | string[] | HTMLElement, title: string = null, defaultAction: PromptAction, dialogArgs: DialogModel, buttons: ButtonPropsModel[]): Dialog {
    const bodyElement = (content as HTMLElement)?.nodeName != null ? content as HTMLElement : null;
    var body = bodyElement != null ? null : this.localeService.textToHtml(content as string | string[]);

    var args: DialogModel = {
      header: title ?? "Meddelelse",
      content: bodyElement ?? body,
      buttons: buttons,
      position: { X: 'center', Y: 'center' },
      isModal: true,
      allowDragging: true,
      animationSettings: { effect: 'Fade', duration: 400, delay: 0 },
      width: 'auto',
      height: 'auto'

    };
    if (dialogArgs != null) {
      args = Object.assign(args, dialogArgs);
    }
    if (this.sfDialog) {
      this.sfDialog.hide();
      this.sfDialog = null;
    }
    this.sfDialog = this.dialogObj(args);
    var dlg: any = this.sfDialog;
    var buttonsEle: HTMLCollection = dlg?.ftrTemplateContent?.children;
    if (buttonsEle) {
      for (let button of <HTMLButtonElement[]>(Array.from(buttonsEle).filter(e => e.tagName == 'BUTTON'))) {
        button.style.textTransform = "none";
      }
    }
    return this.sfDialog;
  }

  private dialogObj(args: DialogModel): Dialog {
    var dialogElement = createDialogElement('div', { 'className': 'e-confirm-dialog' });
    document.body.appendChild(dialogElement);
    const dialogObj = new Dialog(args);
    dialogObj.appendTo(dialogElement);
    dialogObj.close = function () {
      if (args && args.close) {
        args.close.apply(dialogObj);
      }
      dialogObj.destroy();
      if (dialogObj.element.classList.contains('e-dlg-modal')) {
        dialogObj.element.parentElement.remove();
        (dialogObj.target as HTMLElement).classList.remove('e-scroll-disabled');
      }
      else {
        dialogObj.element.remove();
      }
    };
    return dialogObj;
  }
}
